// Feedback Page - Capture artifact feedback from query params
import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ThumbsUp,
  ThumbsDown,
  Send,
  CheckCircle2,
  MessageSquare,
  Sparkles,
  ArrowRight
} from 'lucide-react';
import GoldenSpiral from '../components/GoldenSpiral';
import { trackEvent } from '../services/api';

export default function Feedback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Parse query params from artifact
  const goal = searchParams.get('goal') || '';
  const tools = searchParams.get('tools') || '';
  const ai = searchParams.get('ai') || '';
  const rating = searchParams.get('rating') || ''; // 'up' or 'down'
  const ref = searchParams.get('ref') || 'artifact';

  const [comment, setComment] = useState('');
  const [selectedRating, setSelectedRating] = useState(rating);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Track page view
  useEffect(() => {
    trackEvent('feedback_page_viewed', {
      goal,
      tools,
      ai,
      rating,
      ref
    });
  }, [goal, tools, ai, rating, ref]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Track the feedback submission
      await trackEvent('artifact_feedback_submitted', {
        goal,
        tools: tools.split(',').map(t => t.trim()).filter(Boolean),
        ai,
        rating: selectedRating,
        comment,
        ref
      });

      setIsSubmitted(true);
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      // Still show success - we don't want to frustrate users
      setIsSubmitted(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const toolsList = tools.split(',').map(t => t.trim()).filter(Boolean);

  if (isSubmitted) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full text-center"
        >
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-8 h-8 text-green-600" />
          </div>

          <h1 className="text-2xl font-bold mb-2 text-gray-900">
            Thank you for your feedback!
          </h1>

          <p className="text-gray-600 mb-8">
            Your input helps us improve VinciKit for everyone.
          </p>

          <div className="space-y-3">
            <button
              onClick={() => navigate('/home')}
              className="w-full px-6 py-4 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
            >
              Explore VinciKit
              <ArrowRight className="w-5 h-5" />
            </button>

            <a
              href="https://chatgpt.com"
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full px-6 py-4 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
            >
              Back to ChatGPT
            </a>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Header */}
      <header className="px-6 py-4 border-b border-gray-100 bg-white/80 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto flex items-center justify-center">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-amber-500 via-orange-500 to-yellow-600 rounded-lg flex items-center justify-center">
              <GoldenSpiral className="w-5 h-5" color="white" />
            </div>
            <span className="font-bold text-xl">VinciKit</span>
          </div>
        </div>
      </header>

      <main className="px-6 py-12">
        <div className="max-w-lg mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-medium mb-4">
              <Sparkles className="w-4 h-4" />
              From VinciKit Artifact
            </div>

            <h1 className="text-3xl font-bold text-gray-900 mb-3">
              Share Your Feedback
            </h1>
            <p className="text-lg text-gray-600">
              Help us improve your AI stack building experience
            </p>
          </motion.div>

          {/* Context Card */}
          {(goal || toolsList.length > 0) && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-2xl shadow-xl p-6 mb-6"
            >
              <h3 className="font-semibold text-gray-900 mb-3">Your Stack</h3>
              <div className="space-y-2 text-sm">
                {goal && (
                  <div className="flex items-start gap-2">
                    <span className="text-gray-500 shrink-0">Goal:</span>
                    <span className="font-medium text-gray-900">{decodeURIComponent(goal)}</span>
                  </div>
                )}
                {ai && (
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500">AI:</span>
                    <span className="font-medium text-gray-900">{ai}</span>
                  </div>
                )}
                {toolsList.length > 0 && (
                  <div className="flex items-start gap-2">
                    <span className="text-gray-500 shrink-0">Tools:</span>
                    <div className="flex flex-wrap gap-1">
                      {toolsList.map((tool, i) => (
                        <span
                          key={i}
                          className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs font-medium"
                        >
                          {tool}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Feedback Form */}
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            onSubmit={handleSubmit}
            className="bg-white rounded-2xl shadow-xl p-6"
          >
            {/* Rating */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                How was the recommendation?
              </label>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setSelectedRating('up')}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 transition-all ${
                    selectedRating === 'up'
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  <ThumbsUp className={`w-5 h-5 ${selectedRating === 'up' ? 'fill-current' : ''}`} />
                  Helpful
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedRating('down')}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 transition-all ${
                    selectedRating === 'down'
                      ? 'border-red-500 bg-red-50 text-red-700'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  <ThumbsDown className={`w-5 h-5 ${selectedRating === 'down' ? 'fill-current' : ''}`} />
                  Not helpful
                </button>
              </div>
            </div>

            {/* Comment */}
            <div className="mb-6">
              <label htmlFor="comment" className="block text-sm font-medium text-gray-700 mb-2">
                <MessageSquare className="w-4 h-4 inline mr-1" />
                Tell us more (optional)
              </label>
              <textarea
                id="comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={
                  selectedRating === 'down'
                    ? "What could have been better? Missing tools, wrong recommendations, etc..."
                    : "What did you like? Any suggestions for improvement?"
                }
                rows={4}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              />
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isSubmitting || !selectedRating}
              className="w-full px-6 py-4 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Submit Feedback
                </>
              )}
            </button>
          </motion.form>

          {/* Footer link */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-center text-sm text-gray-500 mt-6"
          >
            Want to build your own stack?{' '}
            <button
              onClick={() => navigate('/home')}
              className="text-blue-600 hover:underline font-medium"
            >
              Try VinciKit
            </button>
          </motion.p>
        </div>
      </main>
    </div>
  );
}
